import React, { useMemo, useEffect, useRef } from "react";
import styled, { keyframes, css } from "styled-components";
import { extractInitials } from "../../Helpers/chatHelper/AvatarHelper";
import { colors, rgb } from "../../Assets/styles/colors";

const RING_DELAYS = [0, 0.5, 1.0];
const BAR_COUNT = 14;
const VOICE_COLOR = rgb(colors.speakingGreen);
const EMOTII_COLOR = rgb(colors.emotiiPurple);

const FrequencyBars = ({ size, isEmotii, isBoth }) => {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const timeRef = useRef(0);
  const isEmotiiRef = useRef(isEmotii);
  const isBothRef = useRef(isBoth);
  useEffect(() => { isEmotiiRef.current = isEmotii; }, [isEmotii]);
  useEffect(() => { isBothRef.current = isBoth;   }, [isBoth]);

  const w = Math.round(size * 0.72);
  const h_main = Math.round(Math.max(16, size * 0.28));
  const h_ref = Math.round(Math.max(6,  size * 0.10));
  const h = h_main + h_ref;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const gap  = Math.max(1, Math.round(w * 0.022));
    const barW = (w - (BAR_COUNT - 1) * gap) / BAR_COUNT;
    const baseY = h_main;

    const drawPill = (x, y, bH) => {
      if (bH < 2) return;
      const br = Math.min(barW / 2, bH / 2);
      ctx.beginPath();
      ctx.moveTo(x + br, y);
      ctx.arcTo(x + barW, y, x + barW, y + bH, br);
      ctx.arcTo(x + barW, y + bH, x, y + bH, br);
      ctx.arcTo(x, y + bH, x, y, br);
      ctx.arcTo(x, y, x + barW, y, br);
      ctx.closePath();
    };

    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      timeRef.current += 0.040;
      const t = timeRef.current;
      for (let i = 0; i < BAR_COUNT; i++) {
        const color = isBothRef.current
          ? (i % 2 === 0 ? VOICE_COLOR : EMOTII_COLOR)
          : (isEmotiiRef.current ? EMOTII_COLOR : VOICE_COLOR);
        const pos  = i / (BAR_COUNT - 1);
        const a1 = Math.sin(pos * Math.PI * 1.5 + t * 1.6) * 0.28;
        const a2 = Math.sin(pos * Math.PI * 3.0 + t * 2.2) * 0.22;
        const a3 = Math.sin(pos * Math.PI * 5.5 + t * 1.0) * 0.16;
        const a4 = Math.sin(pos * Math.PI * 9.0 + t * 3.0) * 0.10;
        const norm = Math.max(0.10, Math.min(1.0, 0.35 + a1 + a2 + a3 + a4));

        const barH = norm * h_main;
        const x = i * (barW + gap);
        const y = baseY - barH;

        const hGrad = ctx.createLinearGradient(x, 0, x + barW, 0);
        hGrad.addColorStop(0, `rgba(${color}, 0.45)`);
        hGrad.addColorStop(0.22, `rgba(${color}, 1.00)`);
        hGrad.addColorStop(0.50, `rgba(${color}, 0.90)`);
        hGrad.addColorStop(0.82, `rgba(${color}, 0.65)`);
        hGrad.addColorStop(1, `rgba(${color}, 0.35)`);

        ctx.shadowColor = `rgba(${color}, 0.45)`;
        ctx.shadowBlur = 6;
        ctx.fillStyle = hGrad;
        drawPill(x, y, barH);
        ctx.fill();

        ctx.shadowBlur = 0;
        const refH = Math.min(barH * 0.40, h_ref - 1);
        const refGrad = ctx.createLinearGradient(0, baseY, 0, baseY + refH);
        refGrad.addColorStop(0, `rgba(${color}, 0.18)`);
        refGrad.addColorStop(1, `rgba(${color}, 0.00)`);
        ctx.fillStyle = refGrad;
        drawPill(x, baseY + 1, refH);
        ctx.fill();
      }

      ctx.shadowBlur = 0;
      rafRef.current = requestAnimationFrame(draw);
    };

    draw();
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [w, h_main, h_ref, h]);

  return <canvas ref={canvasRef} width={w} height={h} style={{ display: "block" }} />;
};

const MINI_BAR_COUNT = 10;

export const MiniWave = ({ isEmotii = false, isBoth = false, small = false }) => {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const timeRef = useRef(0);
  const isEmotiiRef = useRef(isEmotii);
  const isBothRef = useRef(isBoth);
  useEffect(() => { isEmotiiRef.current = isEmotii; }, [isEmotii]);
  useEffect(() => { isBothRef.current = isBoth; }, [isBoth]);

  const w = small ? 28 : 46;
  const h = small ? 9 : 14;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const gap = 1;
    const barW = (w - (MINI_BAR_COUNT - 1) * gap) / MINI_BAR_COUNT;

    const drawPill = (x, y, bH) => {
      if (bH < 2) return;
      const br = Math.min(barW / 2, bH / 2);
      ctx.beginPath();
      ctx.moveTo(x + br, y);
      ctx.arcTo(x + barW, y, x + barW, y + bH, br);
      ctx.arcTo(x + barW, y + bH, x, y + bH, br);
      ctx.arcTo(x, y + bH, x, y, br);
      ctx.arcTo(x, y, x + barW, y, br);
      ctx.closePath();
    };

    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      timeRef.current += 0.05;
      const t = timeRef.current;
      for (let i = 0; i < MINI_BAR_COUNT; i++) {
        const color = isBothRef.current
          ? (i % 2 === 0 ? VOICE_COLOR : EMOTII_COLOR)
          : (isEmotiiRef.current ? EMOTII_COLOR : VOICE_COLOR);
        const pos = i / (MINI_BAR_COUNT - 1);
        const a1 = Math.sin(pos * Math.PI * 1.5 + t * 1.6) * 0.28;
        const a2 = Math.sin(pos * Math.PI * 3.0 + t * 2.2) * 0.22;
        const a3 = Math.sin(pos * Math.PI * 5.5 + t * 1.0) * 0.16;
        const norm = Math.max(0.15, Math.min(1.0, 0.40 + a1 + a2 + a3));

        const barH = norm * h;
        const x = i * (barW + gap);
        const y = h - barH;

        const grad = ctx.createLinearGradient(0, y, 0, y + barH);
        grad.addColorStop(0, `rgba(${color}, 1.0)`);
        grad.addColorStop(1, `rgba(${color}, 0.6)`);
        ctx.fillStyle = grad;
        drawPill(x, y, barH);
        ctx.fill();
      }

      rafRef.current = requestAnimationFrame(draw);
    };

    draw();
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [w, h]);

  return <canvas ref={canvasRef} width={w} height={h} style={{ display: "block" }} />;
};

const AudioWaveformBadge = ({ participant, isSpeaking, isEmotiiSpeaking = false, size = 80 }) => {
  const name = participant?.name || "";
  const profilePicture = participant?.profile_picture || null;
  const initials = useMemo(() => extractInitials(name), [name]);

  const isBoth = isSpeaking && isEmotiiSpeaking;
  const isActive = isSpeaking || isEmotiiSpeaking;
  const color = isEmotiiSpeaking ? EMOTII_COLOR : VOICE_COLOR;

  return (
    <CircleContainer size={size}>
      {isActive && RING_DELAYS.map((delay, i) => (
        <RippleRing
          key={i} delay={delay} size={size}
          color={isBoth ? (i % 2 === 0 ? VOICE_COLOR : EMOTII_COLOR) : color}
        />
      ))}
      <CircleWrapper size={size} $isActive={isActive} $color={color}>
        <Circle size={size}>
          {profilePicture ? (
            <ProfileImg src={profilePicture} alt={name} $hasWaveform={isActive} />
          ) : (
            <InitialsText size={size} $hasWaveform={isActive}>{initials}</InitialsText>
          )}
          {isActive && (
            <>
              <BottomTint />
              <WaveformOverlay size={size}>
                <FrequencyBars size={size} isEmotii={isEmotiiSpeaking} isBoth={isBoth} />
              </WaveformOverlay>
            </>
          )}
        </Circle>
      </CircleWrapper>
    </CircleContainer>
  );
};

export default AudioWaveformBadge;

const ripple = keyframes`
  0% { transform: scale(1); opacity: 0.7; }
  100% { transform: scale(1.9); opacity: 0; }
`;

const glowPulse = (color) => keyframes`
  0%, 100% { box-shadow: 0 0 0 2px rgba(${color}, 0.55), 0 0 8px  2px rgba(${color}, 0.15); }
  50% { box-shadow: 0 0 0 3px rgba(${color}, 1.00), 0 0 22px 6px rgba(${color}, 0.35); }
`;

const CircleContainer = styled.div`
  position: relative;
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const RippleRing = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 2px solid rgba(${({ color }) => color}, 0.6);
  animation: ${ripple} 1.6s ease-out infinite;
  animation-delay: ${({ delay }) => delay}s;
  pointer-events: none;
`;

const CircleWrapper = styled.div`
  border-radius: 50%;
  transition: box-shadow 0.3s ease;
  box-shadow: ${({ $isActive, $color }) =>
    $isActive
      ? `0 0 0 2px rgba(${$color}, 0.8), 0 0 12px 3px rgba(${$color}, 0.2)`
      : "0 0 0 2px rgba(255,255,255,0.12)"};
  ${({ $isActive, $color }) =>
    $isActive &&
    css`
      animation: ${glowPulse($color)} 1.6s ease-in-out infinite;
    `}
`;

const Circle = styled.div`
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
  border-radius: 50%;
  background: ${({ theme }) => theme.menu.avatarBackground};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
  position: relative;
`;

const BottomTint = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 45%;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.55), transparent);
  border-radius: 0 0 50% 50%;
  pointer-events: none;
`;

const WaveformOverlay = styled.div`
  position: absolute;
  bottom: ${({ size }) => Math.max(5, size * 0.08)}px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  pointer-events: none;
`;

const ProfileImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
  transition: transform 0.3s ease;
  transform: ${({ $hasWaveform }) => $hasWaveform ? "translateY(-12%)" : "translateY(0)"};
`;

const InitialsText = styled.span`
  color: white;
  font-weight: 700;
  font-size: ${({ size }) => Math.max(12, size * 0.3)}px;
  line-height: 1;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  user-select: none;
  transition: transform 0.3s ease;
  transform: ${({ $hasWaveform }) => $hasWaveform ? "translateY(-18%)" : "translateY(0)"};
`;
